import { useNavigate } from "#src/Routers/hooks";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useInvestigationsInfiniteList } from "#src/components/hooks/adapters/useInvestigations";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { InvestigationsRoutePath } from "#src/routes/investigations";
import { InvestigationDetailRoutePath } from "#src/routes/investigations/[investigationId]";
import { NewInvestigationRoutePath } from "#src/routes/investigations/new";
import {
  InvestigationStatusLabel,
  InvestigationStatusToPillVariantMapping,
} from "#src/view-models/investigations";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import {
  Button,
  DataTable,
  DataTablePanel,
  Icon,
  Page,
  Pill,
} from "@validereinc/common-components";
import type { InvestigationType } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useMemo } from "react";
import styles from "./InvestigationsListPage.module.scss";

const cx = classNames.bind(styles);

const truncateId = (id: string) => `Investigation ${id.slice(0, 8)}`;

export const InvestigationsListPage = () => {
  const navigate = useNavigate();
  const [breadcrumbs] = useBreadcrumbsFromRoute(InvestigationsRoutePath);
  const [tableConfigStorageKey] = useStorageKey(
    ["table-configuration"],
    "investigations-list"
  );
  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInvestigationsInfiniteList();

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data]
  );

  const isTableBusy =
    (isFetching && !isLoading) || isFetchingNextPage || isLoading;

  const headers = useMemo(
    () => [
      {
        key: "title",
        label: "Title",
        renderComponent: ({ item }) => (
          <RoutingLink
            to={InvestigationDetailRoutePath.toLink({
              pathParams: { investigationId: item.id },
            })}
          >
            {truncateId(item.id)}
          </RoutingLink>
        ),
      },
      {
        key: "status",
        label: "Status",
        renderComponent: ({ item }) => (
          <Pill
            variant={InvestigationStatusToPillVariantMapping[item.status]}
            isLoading={isTableBusy}
          >
            {InvestigationStatusLabel[item.status]}
          </Pill>
        ),
      },
      {
        key: "updated_at",
        label: "Updated",
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell
            value={item.updated_at}
            withTime
            isLoading={isTableBusy}
          />
        ),
      },
    ],
    [isTableBusy]
  );

  const getInvestigationRowId = (item: InvestigationType) => item.id;

  return (
    <Page
      title={InvestigationsRoutePath.title}
      breadcrumbs={breadcrumbs}
      actionRow={[
        <Button
          key="new-investigation"
          variant="primary"
          size="small"
          onClick={() =>
            navigate({ pathname: NewInvestigationRoutePath.toLink() })
          }
          slotLeft={
            <Icon
              variant="plus-circle"
              size={24}
            />
          }
        >
          New Investigation
        </Button>,
      ]}
    >
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          title: "Your Investigations",
        }}
        dataTableProps={{
          items,
          isLoading,
          isBusy: isTableBusy,
          getItemId: getInvestigationRowId,
          headers,
        }}
      />
      {hasNextPage ? (
        <div className={cx("loadMore")}>
          <Button
            variant="outline"
            size="small"
            isLoading={isFetchingNextPage}
            disabled={isFetchingNextPage || isLoading}
            onClick={() => {
              void fetchNextPage();
            }}
          >
            Load more
          </Button>
        </div>
      ) : null}
    </Page>
  );
};
